/* eslint-disable camelcase */
import { BodyText, IconSet } from '@postscript/components';
import React from 'react';

import {
  ClickableRow,
  EnumChip,
  ExpandableRow,
  ExpandableValueStack,
  getProductDisplayTitle,
  LinkValue,
  OfferChip,
  ProductList,
  Row,
  SectionCard,
  SectionHeader,
  ToneOfVoiceSliders,
} from '../components';
import {
  displayMessageType,
  ROW_LABELS,
  SECTION_TITLES,
  URGENCY_LEVERS,
} from '../copy';
import { getContentUrl, MessageDetailsNavigation } from '../navigation';
import { MessageDetails } from '../types';

interface MessageInputsSectionProps {
  details: MessageDetails;
  onNavigate?: Partial<MessageDetailsNavigation>;
}

const SECTION_ID = 'message-details-message-inputs';

// The resolved inputs that drove this generation. Each row renders only when
// its data is present so older messages (or those missing runtime context)
// collapse cleanly. Every configurable input links out (new tab) to the Brand
// Center area that owns it via the optional onNavigate handlers.
export const MessageInputsSection: React.FC<MessageInputsSectionProps> = ({
  details,
  onNavigate = {},
}) => {
  const runtimeContext = details.runtime_context;
  // Per the API contract every runtime_context field is optional, and stored
  // snapshots can carry nulls or empty strings. Rows render from whatever is
  // displayable and disappear entirely when nothing is.
  const displayableProducts = (runtimeContext?.products ?? []).filter(
    (product) => getProductDisplayTitle(product) !== null,
  );
  // Shared with navigation so the displayed URL and the click target can't
  // drift apart.
  const contentUrl = getContentUrl(details);
  // Only levers the message actually used (flag === true); null (blueprint
  // doesn't set the flag) and false both drop the chip, and the row hides
  // entirely when none are enabled.
  const enabledUrgencyLevers = URGENCY_LEVERS.filter(
    ({ key }) => details[key] === true,
  );
  return (
    <SectionCard ariaLabelledBy={SECTION_ID} flush>
      <SectionHeader id={SECTION_ID} icon={IconSet.GenerateMessage} divider>
        {SECTION_TITLES.messageInputs}
      </SectionHeader>

      {details.display_name && (
        <ClickableRow label={ROW_LABELS.tactic} onNavigate={onNavigate.tactic}>
          <LinkValue>
            {details.message_type
              ? `${displayMessageType[details.message_type]} - ${
                  details.display_name
                }`
              : details.display_name}
          </LinkValue>
        </ClickableRow>
      )}

      {runtimeContext?.offer?.title &&
        (runtimeContext.offer.discount_value ||
        runtimeContext.offer.discount_code ? (
          <ExpandableRow
            label={ROW_LABELS.offer}
            summary={<OfferChip offer={runtimeContext.offer} />}
          >
            <ExpandableValueStack>
              {runtimeContext.offer.discount_value && (
                <BodyText size="small">
                  {runtimeContext.offer.discount_value}
                </BodyText>
              )}
              {runtimeContext.offer.discount_code && (
                <BodyText size="small">
                  {runtimeContext.offer.discount_code}
                </BodyText>
              )}
            </ExpandableValueStack>
          </ExpandableRow>
        ) : (
          <Row label={ROW_LABELS.offer} readOnly>
            <OfferChip offer={runtimeContext.offer} />
          </Row>
        ))}

      {contentUrl ? (
        <ClickableRow
          label={ROW_LABELS.content}
          onNavigate={onNavigate.content}
        >
          <LinkValue truncate title={contentUrl}>
            {contentUrl}
          </LinkValue>
        </ClickableRow>
      ) : null}

      {displayableProducts.length === 1 && (
        <ClickableRow
          label={ROW_LABELS.product}
          onNavigate={
            onNavigate.product &&
            (() => onNavigate.product?.(displayableProducts[0].id))
          }
        >
          <LinkValue>
            {getProductDisplayTitle(displayableProducts[0])}
          </LinkValue>
        </ClickableRow>
      )}

      {displayableProducts.length > 1 && (
        <ExpandableRow
          label={ROW_LABELS.product}
          summary={
            <span>
              {getProductDisplayTitle(displayableProducts[0])}
              {` +${displayableProducts.length - 1}`}
            </span>
          }
        >
          <ExpandableValueStack>
            <ProductList
              products={displayableProducts}
              onProductClick={onNavigate.product}
            />
          </ExpandableValueStack>
        </ExpandableRow>
      )}

      {enabledUrgencyLevers.length > 0 && (
        <ClickableRow
          label={ROW_LABELS.urgencyLevers}
          onNavigate={onNavigate.urgencyLevers}
        >
          {enabledUrgencyLevers.map(({ key, label }) => (
            <EnumChip key={key}>{label}</EnumChip>
          ))}
        </ClickableRow>
      )}

      {runtimeContext?.tone_of_voice && (
        <ExpandableRow label={ROW_LABELS.toneOfVoice}>
          <ToneOfVoiceSliders
            tone={runtimeContext.tone_of_voice}
            onAxisClick={onNavigate.toneOfVoice}
          />
        </ExpandableRow>
      )}
    </SectionCard>
  );
};
